import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

export interface GenieAnswersPanelProps {
  /** All sources currently in scope (a Genie's library, or the whole workspace). */
  documents: DocumentWithMetadata[];
  /** Opens the source drawer for the document a clicked question came from. */
  onOpenDocument: (document: DocumentWithMetadata) => void;
  /** Kicks off summarising every not-yet-read source. */
  onSummariseAll: () => void;
  /** How many sources are mid-summarise right now, for progress copy. */
  summarisingCount?: number;
  /** How many sources are queued to be summarised in this run. */
  totalToSummarise?: number;
}

interface AnswerEntry {
  id: string;
  question: string;
  document: DocumentWithMetadata;
  sourceName: string;
}

interface AnswerGroup {
  key: string;
  label: string;
  entries: AnswerEntry[];
}

/** Trailing bucket for questions whose source has no topic tags. Not a guess — a plain fallback. */
const OTHER_GROUP_KEY = "__other__";
const OTHER_GROUP_LABEL = "Other";

/** Roughly how many rows show before the panel asks you to expand. */
const VISIBLE_CAP = 10;

const getAnswers = (document: DocumentWithMetadata): string[] => {
  const raw = document.metadata?.answers;
  return Array.isArray(raw) ? raw.filter((value): value is string => typeof value === "string") : [];
};

const getTopics = (document: DocumentWithMetadata): string[] => {
  const raw = document.metadata?.topics;
  return Array.isArray(raw) ? raw.filter((value): value is string => typeof value === "string") : [];
};

const isSummarised = (document: DocumentWithMetadata): boolean =>
  Boolean(document.metadata?.summarised_at);

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/** Small inline "Reading source X of Y…" readout, reused wherever a summarise action is in flight. */
const SummariseProgress = ({
  summarisingCount,
  totalToSummarise,
}: {
  summarisingCount?: number;
  totalToSummarise?: number;
}) => (
  <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
    <Loader2 className="h-3.5 w-3.5 animate-spin text-genie-primary" aria-hidden />
    {totalToSummarise
      ? `Reading source ${summarisingCount} of ${totalToSummarise}…`
      : "Reading sources…"}
  </span>
);

/**
 * GenieAnswersPanel — "Everything your Genie can answer".
 *
 * Flattens every question captured in each document's `metadata.answers`
 * into one scannable, searchable, click-to-source list. Filenames and a
 * truncated blurb make you open documents one at a time to learn anything;
 * this is the layer a person can absorb at a glance instead.
 *
 * Summarising is on-demand, so most sources typically have no answers yet.
 * The panel leads with that truth rather than looking empty or broken — see
 * the three coverage states below. Nothing here is ever invented: a question,
 * a count, or a topic that is not in `metadata` does not appear on screen.
 *
 * Grouping: each question is filed under its source document's *first*
 * topic tag (`metadata.topics[0]`), so a question never appears in more than
 * one group and the per-group counts stay exact. Sources with no topics (or
 * no answers yet) never get a made-up topic — their questions land in a
 * single trailing "Other" group instead.
 *
 * Compactness: with topics present, the panel always renders the full
 * grouped, collapsible structure — group headers and their true counts are
 * never truncated. What IS capped, until "Show all" is pressed, is how many
 * question rows render inside those groups (~{@link VISIBLE_CAP} total), so
 * a library with hundreds of answers still opens compact.
 */
export const GenieAnswersPanel = ({
  documents,
  onOpenDocument,
  onSummariseAll,
  summarisingCount,
  totalToSummarise,
}: GenieAnswersPanelProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const totalSources = documents.length;

  const summarisedCount = useMemo(
    () => documents.filter(isSummarised).length,
    [documents],
  );
  const unsummarisedCount = totalSources - summarisedCount;
  const isSummarising = typeof summarisingCount === "number" && summarisingCount > 0;

  // One group per first-seen topic, in first-seen order, "Other" always last.
  const groups = useMemo<AnswerGroup[]>(() => {
    const order: string[] = [];
    const byKey = new Map<string, AnswerGroup>();

    documents.forEach((document) => {
      const answers = getAnswers(document);
      if (answers.length === 0) return;

      const topics = getTopics(document);
      const topic = topics[0]?.trim();
      const key = topic || OTHER_GROUP_KEY;
      const label = topic || OTHER_GROUP_LABEL;

      if (!byKey.has(key)) {
        byKey.set(key, { key, label, entries: [] });
        order.push(key);
      }

      const group = byKey.get(key)!;
      answers.forEach((question, index) => {
        group.entries.push({
          id: `${document.id}-${index}`,
          question,
          document,
          sourceName: document.name,
        });
      });
    });

    const ordered = order.filter((key) => key !== OTHER_GROUP_KEY);
    if (byKey.has(OTHER_GROUP_KEY)) ordered.push(OTHER_GROUP_KEY);

    return ordered.map((key) => byKey.get(key)!);
  }, [documents]);

  const totalQuestions = useMemo(
    () => groups.reduce((sum, group) => sum + group.entries.length, 0),
    [groups],
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const isSearching = normalizedSearch.length > 0;

  const filteredGroups = useMemo<AnswerGroup[]>(() => {
    if (!isSearching) return groups;
    return groups
      .map((group) => ({
        ...group,
        entries: group.entries.filter(
          (entry) =>
            entry.question.toLowerCase().includes(normalizedSearch) ||
            entry.sourceName.toLowerCase().includes(normalizedSearch),
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [groups, isSearching, normalizedSearch]);

  const filteredCount = useMemo(
    () => filteredGroups.reduce((sum, group) => sum + group.entries.length, 0),
    [filteredGroups],
  );

  // Search always shows every match — capping it would defeat the two-second
  // "can it answer X" lookup this box exists for. Otherwise, keep the true
  // group headers/counts but trim the rows inside them down to VISIBLE_CAP
  // until the user asks to see the rest.
  const renderGroups = useMemo(() => {
    const showEverything = isSearching || isExpanded;
    let remaining = VISIBLE_CAP;

    return filteredGroups.map((group) => {
      const visibleEntries = showEverything ? group.entries : group.entries.slice(0, Math.max(remaining, 0));
      remaining -= visibleEntries.length;
      return { group, visibleEntries };
    });
  }, [filteredGroups, isExpanded, isSearching]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showExpanderControl = !isSearching && filteredCount > VISIBLE_CAP;

  const headerLine =
    totalSources === 0
      ? "No sources yet."
      : summarisedCount === totalSources
        ? `${pluralize(totalQuestions, "question")} from ${pluralize(summarisedCount, "source")}.`
        : `${pluralize(totalQuestions, "question")} from ${summarisedCount} of ${pluralize(
            totalSources,
            "source",
          )} — ${pluralize(unsummarisedCount, "source")} haven't been read yet.`;

  return (
    <section
      className="mb-6 rounded-2xl border border-gray-200 bg-white"
      aria-labelledby="genie-answers-heading"
    >
      <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-start sm:justify-between md:px-6">
        <div className="flex items-start gap-3">
          <GenieIcon
            name="lightbulb"
            size={24}
            color="#4E9CFF"
            secondaryColor="#FF4DB8"
            gradientEnabled
            label="Everything your Genie can answer"
            className="mt-0.5 flex-shrink-0"
          />
          <div>
            <h2 id="genie-answers-heading" className="text-base font-semibold text-gray-900">
              Everything your Genie can answer
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">{headerLine}</p>
          </div>
        </div>

        {/* Only the mixed-coverage state gets a header action — the "none summarised"
            invitation keeps its call to action inside the body, and "fully summarised"
            has nothing left to offer here. */}
        {summarisedCount > 0 && unsummarisedCount > 0 && (
          <div className="self-start sm:self-auto">
            {isSummarising ? (
              <SummariseProgress summarisingCount={summarisingCount} totalToSummarise={totalToSummarise} />
            ) : (
              <Button
                onClick={onSummariseAll}
                className="h-9 gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary px-4 text-xs font-semibold text-white shadow-md shadow-genie-primary/20 hover:from-genie-primary-hover hover:to-genie-secondary"
              >
                Summarise the rest
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-5 md:px-6">
        {totalSources === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
            Add a source and the questions it can answer will show up here.
          </p>
        ) : summarisedCount === 0 ? (
          <div className="rounded-xl border border-dashed border-genie-primary/30 bg-genie-primary/5 px-5 py-8 text-center">
            <p className="mx-auto max-w-md text-sm text-gray-600">
              Summarising each source lets you see everything your Genie can answer, at a
              glance, without opening a single file.
            </p>
            <div className="mt-4 flex justify-center">
              {isSummarising ? (
                <SummariseProgress
                  summarisingCount={summarisingCount}
                  totalToSummarise={totalToSummarise}
                />
              ) : (
                <Button
                  onClick={onSummariseAll}
                  className="gap-2 bg-gradient-to-r from-genie-primary to-genie-secondary px-5 text-sm font-semibold text-white shadow-md shadow-genie-primary/20 hover:from-genie-primary-hover hover:to-genie-secondary"
                >
                  Summarise all sources
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <label className="relative mb-4 block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search what your Genie can answer…"
                className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-genie-primary"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </label>

            {filteredCount === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500">
                No questions match &ldquo;{searchTerm}&rdquo;.
              </p>
            ) : (
              <div className="space-y-4">
                {renderGroups.map(({ group, visibleEntries }) => {
                  if (visibleEntries.length === 0) return null;
                  const isCollapsed = collapsedGroups.has(group.key);

                  return (
                    <div key={group.key}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.key)}
                        aria-expanded={!isCollapsed}
                        className="flex w-full items-center gap-2 rounded-lg py-1.5 text-left transition-colors hover:bg-gray-50"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" aria-hidden />
                        )}
                        <span className="text-sm font-semibold text-gray-900">{group.label}</span>
                        <span className="text-xs text-gray-500">
                          ({pluralize(group.entries.length, "question")})
                        </span>
                      </button>

                      {!isCollapsed && (
                        <ul className="mt-1 space-y-1">
                          {visibleEntries.map((entry) => (
                            <li key={entry.id}>
                              <button
                                type="button"
                                onClick={() => onOpenDocument(entry.document)}
                                className={cn(
                                  "flex min-h-[40px] w-full flex-col justify-center rounded-lg px-3 py-2 text-left transition-colors",
                                  "hover:bg-genie-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary",
                                )}
                              >
                                <span className="text-sm font-medium leading-snug text-gray-900">
                                  {entry.question}
                                </span>
                                <span className="mt-0.5 text-xs text-gray-500">{entry.sourceName}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {showExpanderControl && (
              <button
                type="button"
                onClick={() => setIsExpanded((value) => !value)}
                className="mt-3 text-xs font-semibold text-genie-primary hover:underline"
              >
                {isExpanded ? "Show fewer" : `Show all ${filteredCount} questions`}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default GenieAnswersPanel;
